"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Calendar } from "lucide-react";
function LatestUpdatesConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div data-section-id="56153" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {[1, 2, 3].map((item, i) => <div key={i} data-index={i} className="h-[420px] rounded-3xl bg-muted/40 animate-pulse border border-border/50" />)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-12 bg-destructive/5 rounded-3xl border border-destructive/20">
        <p className="text-destructive font-medium">Unable to load wellness articles at this time.</p>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="text-center py-12 bg-muted/30 rounded-3xl border border-border">
        <p className="text-muted-foreground">Check back soon for new health and wellness insights.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      {posts.map((post, i) => {
      const dateObj = post.date ? new Date(post.date) : new Date();
      const month = dateObj.toLocaleDateString('en-US', {
        month: 'short'
      });
      const day = dateObj.getDate();
      return <Card key={post.id} data-index={i} className="group flex flex-col h-full overflow-hidden border-border/50 bg-card/50 backdrop-blur-sm hover:shadow-xl hover:border-primary/30 rounded-3xl transition-all duration-500">
            {}
            <div className="relative aspect-[16/10] overflow-hidden bg-muted">
              {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" /> : <div className="w-full h-full flex items-center justify-center bg-accent/20 text-muted-foreground">
                  <SafeHydrate><Calendar className="w-12 h-12 opacity-20" /></SafeHydrate>
                </div>}
              
              {}
              <div className="absolute top-4 left-4 z-10">
                <Badge variant="secondary" className="flex flex-col items-center justify-center px-4 py-2 h-auto min-w-[64px] bg-background/90 backdrop-blur-xl shadow-md border-border/40 rounded-2xl gap-0">
                  <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-primary">
                    {month}
                  </span>
                  <span className="text-2xl font-serif font-bold text-foreground leading-none mt-1">
                    {day}
                  </span>
                </Badge>
              </div>
            </div>

            <CardHeader className="pt-8 pb-3 px-8">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-[10px] font-mono font-bold tracking-widest uppercase text-secondary">
                  {post.categories && post.categories.length > 0 ? post.categories[0].name : 'Wellness'}
                </span>
              </div>
              <CardTitle className="text-2xl font-serif font-bold leading-tight line-clamp-2 group-hover:text-primary transition-colors duration-300">
                <Link to={post.link || "#"}>
                  {post.title}
                </Link>
              </CardTitle>
            </CardHeader>

            <CardContent className="px-8 pb-6 flex-grow">
              <div className="text-muted-foreground text-base leading-relaxed line-clamp-3 font-sans" dangerouslySetInnerHTML={{
            __html: post.excerpt || ""
          }} />
            </CardContent>

            <CardFooter className="px-8 pb-8 pt-0 mt-auto">
              <Link to={post.link || "#"} className="w-full">
                <Button variant="ghost" className="w-full justify-between pl-0 hover:pl-2 hover:bg-transparent hover:text-primary text-primary transition-all duration-300 group/btn">
                  <span className="font-bold tracking-tight">Read Wellness Guide</span>
                  <ArrowRight className="w-5 h-5 ml-2 transition-transform duration-300 group-hover/btn:translate-x-2" />
                </Button>
              </Link>
            </CardFooter>
          </Card>;
    })}
    </div>;
}
export default function LatestCampusUpdates() {
  const wp_query_params = {
    per_page: 3,
    orderby: "date",
    order: "desc",
    status: "publish"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="health-hub-education" className="py-24 bg-background relative overflow-hidden">
      {}
      <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-accent opacity-20 blur-[120px] rounded-full -translate-y-1/2 translate-x-1/2" />
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {}
        <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-8 border-b border-border/60 pb-10">
          <div className="max-w-2xl">
            <h2 className="text-4xl md:text-5xl font-serif font-bold tracking-tight text-foreground mb-6">
              Health Hub & Education
            </h2>
            <p className="text-xl text-muted-foreground leading-relaxed font-sans">
              Expert medical insights and family wellness tips from our dedicated healthcare team at Freetown Family Clinic.
            </p>
          </div>
          <div className="hidden md:block shrink-0">
            <Link to="#">
              <Button size="lg" className="rounded-full px-10 shadow-lg bg-primary hover:bg-primary/90 text-primary-foreground font-bold transition-all duration-300 hover:scale-105">
                Browse All Articles
              </Button>
            </Link>
          </div>
        </div>

        {}
        <PostsProvider wp_query={{
        per_page: 3,
        orderby: "date",
        order: "desc",
        status: ["publish"]
      }} className="w-full">
          <LatestUpdatesConsumer />
        </PostsProvider>

        {}
        <div className="mt-12 md:hidden flex justify-center">
          <Link to="#" className="w-full sm:w-auto">
            <Button size="lg" className="w-full rounded-full px-10 shadow-lg bg-primary hover:bg-primary/90 text-primary-foreground font-bold transition-all duration-300">
              Browse All Articles
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}